import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { api } from '../services/api';
import { businessToday, isoPlusDays } from '../utils/time';

/**
 * Who sees what today, and why — the crew-app stop list explained per person
 * (owner, Sep 28 2026: "one is seeing 17, one is seeing 14"). Renders
 * GET /jobs/visibility-report: each member's stops with the rule showing
 * them, and the problem rows (twins, off-cadence, dead series) on top.
 */
export default function VisibilityReport() {
  const [day, setDay] = useState(businessToday());
  const { data, isLoading, isError } = useQuery(['visibility-report', day],
    () => api.get('/jobs/visibility-report', { params: { date: day } }).then(r => r.data));
  const dayWords = new Date(`${day}T12:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });

  return (
    <div className="space-y-4 max-w-2xl mx-auto">
      <div>
        <h1 className="text-2xl font-black uppercase tracking-tight text-gray-900">Who sees what</h1>
        <p className="text-xs text-gray-500">Every stop on each crew phone, in the order the phone shows it, with the rule showing it — and the rows that shouldn't exist.</p>
      </div>

      <div className="flex items-center gap-2">
        <button onClick={() => setDay(isoPlusDays(day, -1))} className="px-3 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700">‹</button>
        <label className="flex-1 relative">
          <span className="block text-center py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-800">{day === businessToday() ? `Today · ${dayWords}` : dayWords}</span>
          <input type="date" value={day} onChange={e => e.target.value && setDay(e.target.value)} className="absolute inset-0 opacity-0 cursor-pointer" aria-label="Pick a day" />
        </label>
        <button onClick={() => setDay(isoPlusDays(day, 1))} className="px-3 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700">›</button>
      </div>

      {isLoading && <p className="text-sm text-gray-500">Reading the day…</p>}
      {isError && <p className="text-sm text-red-600">Could not build the report.</p>}

      {data && (
        <>
          {(data.problems || []).length > 0 ? (
            <div className="card p-0 overflow-hidden border-red-200">
              <p className="px-3 py-2 text-xs font-bold uppercase tracking-wide text-red-700 bg-red-50 border-b border-red-100">
                {data.problems.length} row{data.problems.length === 1 ? '' : 's'} that shouldn't be there
              </p>
              <div className="divide-y divide-gray-50">
                {data.problems.map((p: any, i: number) => (
                  <div key={i} className="px-3 py-2 text-sm">
                    <p className="font-semibold text-gray-900">{p.stop} <span className="text-gray-400 font-normal">· job {p.job_id}</span></p>
                    <p className="text-xs font-bold text-red-700">{p.verdict}</p>
                    <p className="text-[11px] text-gray-500">{p.detail}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="card p-3 text-sm text-emerald-700">No twins, nothing off-cadence — every row belongs on this day.</p>
          )}

          {(data.members || []).map((m: any) => (
            <details key={m.crew_member} className="card p-0 overflow-hidden" open={(data.members || []).length <= 3}>
              <summary className="px-3 py-2.5 cursor-pointer flex items-baseline justify-between">
                <span className="text-sm font-bold text-gray-900">{m.crew_member}</span>
                <span className="text-xs font-semibold text-gray-600">sees {m.sees} stop{m.sees === 1 ? '' : 's'}</span>
              </summary>
              <div className="divide-y divide-gray-50 border-t border-gray-100">
                {m.stops.map((s: any) => (
                  <div key={s.job_id} className="px-3 py-1.5 text-sm flex items-baseline gap-2">
                    <span className={`flex-1 min-w-0 truncate ${s.status === 'complete' ? 'text-gray-400' : 'text-gray-900'}`}>
                      {s.seq ? <span className="text-gray-500 tabular-nums">{s.seq}. </span> : null}{s.stop}
                      {s.kind && s.kind !== 'mowing' ? <span className="text-gray-500"> · {String(s.kind).replace(/_/g, ' ')}</span> : null}
                      <span className="text-gray-400"> · #{s.job_id}</span>
                    </span>
                    <span className="text-[11px] text-gray-500 text-right">{s.reasons.join(' + ')}</span>
                  </div>
                ))}
              </div>
            </details>
          ))}
          {!(data.members || []).length && <p className="card p-3 text-sm text-gray-500">No crew member sees anything on this day.</p>}
        </>
      )}
    </div>
  );
}
